import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Copy, UserPlus, Ban, ChevronLeft, ChevronRight, Loader2, FileSpreadsheet, Mail,
  ChevronsUpDown, ChevronUp, ChevronDown, Share2, Archive,
} from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AdminRefreshButton } from "@/components/admin/AdminRefreshButton";

const TIERS = [
  { value: "standard", label: "VIP" },
  { value: "pro", label: "PRO" },
  { value: "premium", label: "CAPITAL" },
  { value: "business", label: "Teams" },
];
const TIER_LABEL = Object.fromEntries(TIERS.map((t) => [t.value, t.label]));
const STATUSES = ["all", "available", "issued", "active", "disabled", "superseded", "revoked"];
const SCOPES = [
  { value: "inventory", label: "Inventory · assign later" },
  { value: "claimable", label: "Claimable · shareable" },
];
const SCOPE_LABEL = { inventory: "Inventory", assigned: "Assigned", claimable: "Claimable" };
const SCOPE_TONE = {
  inventory: "bg-slate-100 text-slate-600", assigned: "bg-indigo-50 text-indigo-700",
  claimable: "bg-emerald-50 text-emerald-700",
};
const REASONS = [
  { value: "none", label: "— No reason —" },
  { value: "paid", label: "Paid" },
  { value: "student", label: "Student" },
  { value: "founding-member", label: "Founding member" },
  { value: "partner", label: "Partner" },
  { value: "scholarship", label: "Scholarship" },
  { value: "community", label: "Community" },
  { value: "promo", label: "Promo" },
  { value: "lifetime", label: "Lifetime" },
  { value: "admin-comp", label: "Admin comp" },
  { value: "other", label: "Other" },
];
const REASON_LABEL = Object.fromEntries(REASONS.map((r) => [r.value, r.label]));
const EXPORT_CAP = 5000;   // mirrors services/licenses.py — one file can't drain the vault
const BATCH_PAGE_SIZE = 5; // the batches card stays short; older mints are a page away
const STATUS_TONE = {
  available: "bg-emerald-50 text-emerald-700", issued: "bg-amber-50 text-amber-700",
  active: "bg-teal-50 text-teal-700", disabled: "bg-secondary text-muted-foreground",
  superseded: "bg-secondary text-muted-foreground", revoked: "bg-red-50 text-red-700",
};

function Pill({ tone, children, testId }) {
  return <span data-testid={testId} className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-[640] ${tone || "bg-secondary"}`}>{children}</span>;
}

function AssignDialog({ open, onOpenChange, onAssign }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const handleOpenChange = (v) => {
    if (!v) { setQ(""); setResults([]); }
    onOpenChange(v);
  };
  useEffect(() => {
    if (!q.trim()) return;
    let active = true;
    const t = setTimeout(async () => {
      const { data } = await api.get("/admin/users", { params: { q, page_size: 6 } }).catch(() => ({ data: { users: [] } }));
      if (active) setResults(data.users || []);
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [q]);
  const shown = q.trim() ? results : [];
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="license-assign-dialog">
        <DialogHeader>
          <DialogTitle>Issue key to a member</DialogTitle>
          <DialogDescription>Search by name or email. They&apos;ll be notified to activate it. Assigning makes the key redeemable only by that account.</DialogDescription>
        </DialogHeader>
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members…" data-testid="license-assign-search" />
        <div className="mt-2 max-h-64 space-y-1 overflow-y-auto">
          {shown.map((u) => (
            <button key={u.user_id} onClick={() => onAssign(u)} className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-secondary" data-testid={`license-assign-pick-${u.user_id}`}>
              <span>{u.first_name} {u.last_name}<span className="ml-2 text-xs text-muted-foreground">{u.email}</span></span>
              <span className="text-xs text-muted-foreground">{TIER_LABEL[u.tier] || u.tier}</span>
            </button>
          ))}
          {q && shown.length === 0 && <p className="px-3 py-2 text-sm text-muted-foreground">No matches.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const SORT_COLUMNS = [
  { key: "key", label: "Key" },
  { key: "tier", label: "Tier" },
  { key: "status", label: "Status" },
  { key: "scope", label: "Scope" },
  { key: "reason", label: "Reason" },
  { key: "recipient", label: "Recipient / note" },
  { key: "holder", label: "Holder" },
  { key: "issued", label: "Issued / Expires" },
];

function SortHead({ col, sort, dir, onSort }) {
  const active = sort === col.key;
  const Icon = !active ? ChevronsUpDown : dir === "asc" ? ChevronUp : ChevronDown;
  return (
    <TableHead className="whitespace-nowrap">
      <button type="button" onClick={() => onSort(col.key)}
              aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
              className={`-ml-1 inline-flex items-center gap-1 rounded px-1 py-0.5 transition-colors hover:text-foreground ${active ? "text-foreground" : "text-muted-foreground"}`}
              data-testid={`license-sort-${col.key}`}>
        {col.label}
        <Icon className={`h-3.5 w-3.5 transition-opacity ${active ? "opacity-100" : "opacity-40"}`} />
      </button>
    </TableHead>
  );
}

function EmailBatchDialog({ batch, onClose, onSent }) {
  const [to, setTo] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => { setTo(""); setName(""); setMessage(""); }, [batch?.batch_id]);
  if (!batch) return null;
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/licenses/batches/${batch.batch_id}/email`, {
        to, recipient_name: name || undefined, message: message || undefined,
      });
      if (data.delivered) {
        toast.success(`Sent ${data.sent} key(s) to ${data.to} as ${data.filename}.`);
        onSent();
      } else if (data.mode === "log") {
        // No provider key in this environment — the email was recorded, never delivered.
        toast.warning("Recorded but NOT delivered — this environment has no email provider key. Download the CSV and send it manually, or try from production.", { duration: 9000 });
      } else {
        toast.error(`The email provider rejected this send${data.error ? ` (${data.error})` : ""}. Nothing was delivered.`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="license-email-dialog">
        <DialogHeader>
          <DialogTitle>Email this batch</DialogTitle>
          <DialogDescription>
            The keys go out as a CSV attachment{batch.recipient_org ? ` for ${batch.recipient_org}` : ""}. Anyone with a key can redeem it, so send it only to the right contact. Wording lives in Admin → Emails → &quot;License key batch delivery&quot;.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Send to</Label>
            <Input type="email" autoFocus value={to} onChange={(e) => setTo(e.target.value)}
                   placeholder="partner@company.com" data-testid="license-email-to" />
          </div>
          <div className="space-y-1.5">
            <Label>Their name <span className="text-muted-foreground">(optional)</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120}
                   placeholder="Dana" data-testid="license-email-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Add a note <span className="text-muted-foreground">(optional)</span></Label>
            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={1000}
                      placeholder="Anything you want them to know." data-testid="license-email-message" />
          </div>
          <Button onClick={send} disabled={sending || !to.trim()} data-testid="license-email-send">
            {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />}
            Send {batch.quantity || batch.minted} key{(batch.quantity || batch.minted) === 1 ? "" : "s"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminLicenses() {
  const [mintTier, setMintTier] = useState("pro");
  const [mintQty, setMintQty] = useState(5);
  const [mintScope, setMintScope] = useState("inventory");
  const [mintReason, setMintReason] = useState("none");
  const [mintDuration, setMintDuration] = useState("lifetime");
  const [mintExpires, setMintExpires] = useState("");
  const [mintOrg, setMintOrg] = useState("");
  const [mintNote, setMintNote] = useState("");
  const [minting, setMinting] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);
  const [batches, setBatches] = useState([]);
  const [batchPage, setBatchPage] = useState(1);
  const [batchTotal, setBatchTotal] = useState(0);
  const [exporting, setExporting] = useState("");
  const [emailFor, setEmailFor] = useState(null);
  const [revokeFor, setRevokeFor] = useState(null);
  const [sort, setSort] = useState("issued");
  const [dir, setDir] = useState("desc");
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("all");
  const [tier, setTier] = useState("all");
  const [scope, setScope] = useState("all");
  const [q, setQ] = useState("");
  const [assignFor, setAssignFor] = useState(null);
  // Key Conversion: unclaimed keys can be retargeted between Inventory and Claimable.
  const [sel, setSel] = useState([]);
  const [convertTo, setConvertTo] = useState(null);      // "claimable" | "inventory" | null
  const [convertBatch, setConvertBatch] = useState(null); // set when converting a whole batch
  const [convertExpiry, setConvertExpiry] = useState("");
  const [convertNoExpiry, setConvertNoExpiry] = useState(false);
  const [converting, setConverting] = useState(false);
  const [converted, setConverted] = useState(null);      // { count, ids, scope }
  const pageSize = 25;

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/licenses", { params: { status, tier, scope, q, page, page_size: pageSize, sort, dir } });
    setRows(data.keys); setTotal(data.total); setCounts(data.counts || {});
  }, [status, tier, scope, q, page, sort, dir]);

  // Clicking a heading sorts by it; clicking the active one flips direction.
  const toggleSort = (key) => {
    setPage(1);
    if (sort === key) { setDir((d) => (d === "asc" ? "desc" : "asc")); return; }
    setSort(key);
    setDir(key === "issued" ? "desc" : "asc");
  };

  const loadBatches = useCallback(async () => {
    const { data } = await api.get("/admin/licenses/batches", { params: { page: batchPage, page_size: BATCH_PAGE_SIZE } })
      .catch(() => ({ data: { batches: [], total: 0 } }));
    setBatches(data.batches || []);
    setBatchTotal(data.total || 0);
  }, [batchPage]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  // A selection only means something for the rows on screen.
  useEffect(() => { setSel([]); }, [status, tier, scope, q, page, sort, dir]);
  useEffect(() => { loadBatches(); }, [loadBatches]);

  const download = async (params, batchId) => {
    setExporting(batchId || "filters");
    try {
      const res = await api.get("/admin/licenses/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const match = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : "license-keys.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Keys exported — the file contains live keys, keep it secure.");
      loadBatches();
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "No keys match — nothing to export." : "Export failed");
    } finally { setExporting(""); }
  };

  const revokeBatch = async () => {
    const batch = revokeFor;
    setRevokeFor(null);
    try {
      const { data } = await api.post(`/admin/licenses/batches/${batch.id}/revoke`, { reason: "batch revoked" });
      toast.success(`Revoked ${data.revoked} unredeemed key(s).`);
      loadBatches(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
  };

  const mint = async () => {
    if (mintDuration === "expires" && !mintExpires) { toast.error("Pick an expiry date for a time-limited key."); return; }
    setMinting(true);
    try {
      const body = {
        tier_key: mintTier, quantity: Number(mintQty), redeem_scope: mintScope,
        duration_type: mintDuration,
      };
      if (mintReason !== "none") body.reason_issued = mintReason;
      if (mintNote.trim()) body.notes = mintNote.trim();
      if (mintOrg.trim()) body.recipient_org = mintOrg.trim();
      if (mintDuration === "expires" && mintExpires) body.expires_at = new Date(mintExpires).toISOString();
      const { data } = await api.post("/admin/licenses/mint", body);
      toast.success(`Minted ${data.minted} ${TIER_LABEL[mintTier]} key(s) · ${SCOPE_LABEL[mintScope]}.`);
      setLastBatch({ ...data, tier_key: mintTier });
      setPage(1); setStatus("available"); setTier(mintTier); setScope("all"); load(); loadBatches();
    } catch (e) { toast.error(e?.response?.data?.detail || "Mint failed"); }
    finally { setMinting(false); }
  };

  // ---- Key Conversion ------------------------------------------------------
  // Only unclaimed stock can be retargeted; the same rule is enforced server-side.
  const convertible = (k) => k.status === "available"
    && (k.redeem_scope === "inventory" || k.redeem_scope === "claimable");
  const eligible = (rows || []).filter(convertible);
  const allSelected = eligible.length > 0 && eligible.every((k) => sel.includes(k.id));
  const toggleOne = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSel(allSelected ? [] : eligible.map((k) => k.id));
  const openConvert = (target, batch = null) => {
    setConvertTo(target);
    setConvertBatch(batch);
    setConvertExpiry("");
    setConvertNoExpiry(false);
  };
  const convertCount = convertBatch ? convertBatch.remaining : sel.length;
  const runConvert = async () => {
    if (!convertCount) { setConvertTo(null); return; }
    if (convertTo === "claimable" && !convertNoExpiry && !convertExpiry) {
      toast.error("Pick a redeem-by date, or choose no expiry.");
      return;
    }
    setConverting(true);
    try {
      const body = { redeem_scope: convertTo };
      if (convertTo === "claimable" && !convertNoExpiry) body.redeem_by = new Date(convertExpiry).toISOString();
      const url = convertBatch
        ? `/admin/licenses/batches/${convertBatch.id}/convert-scope`
        : "/admin/licenses/convert-scope";
      if (!convertBatch) body.key_ids = sel;
      const { data } = await api.post(url, body);
      if (!data.converted) {
        toast.error("Nothing converted — those keys are already claimed, assigned or revoked.");
      } else {
        toast.success(`Converted ${data.converted} key(s) to ${SCOPE_LABEL[convertTo]}${data.skipped ? ` · ${data.skipped} skipped` : ""}.`);
        setConverted({ count: data.converted, ids: data.key_ids || [], scope: convertTo });
      }
      setConvertTo(null); setConvertBatch(null); setSel([]); load(); loadBatches();
    } catch (e) { toast.error(e?.response?.data?.detail || "Conversion failed"); }
    finally { setConverting(false); }
  };

  const copy = (k) => {
    Promise.resolve(navigator.clipboard?.writeText(k))
      .then(() => toast.success("Key copied."))
      .catch(() => toast.error("Couldn't copy — copy it manually."));
  };
  const revoke = async (id) => {
    try { await api.post(`/admin/licenses/${id}/revoke`, { reason: "admin revoke" }); toast.success("Key revoked."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Revoke failed"); }
  };
  const assign = async (user) => {
    try {
      await api.post(`/admin/licenses/${assignFor.id}/assign`, { user_id: user.user_id });
      toast.success(`Issued to ${user.first_name}. They've been notified.`);
      setAssignFor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid="admin-licenses">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-[680] text-foreground">License keys</h2>
          <p className="text-sm text-muted-foreground">Mint redeemable keys and track the full inventory. <b>Inventory</b> keys must be assigned before a member can redeem them; <b>claimable</b> keys can be shared and self-redeemed once.</p>
        </div>
        <AdminRefreshButton testId="admin-refresh-licenses" onRefresh={load} />
      </div>

      {/* Mint */}
      <Card className="mt-5 flex flex-wrap items-end gap-4 p-5">
        <div className="space-y-1.5">
          <Label>Tier</Label>
          <Select value={mintTier} onValueChange={setMintTier}>
            <SelectTrigger className="w-36" data-testid="mint-tier"><SelectValue /></SelectTrigger>
            <SelectContent>{TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Quantity</Label>
          <Input type="number" min="1" max="500" value={mintQty} onChange={(e) => setMintQty(e.target.value)} className="w-24" data-testid="mint-qty" />
        </div>
        <div className="space-y-1.5">
          <Label>Redeem scope</Label>
          <Select value={mintScope} onValueChange={setMintScope}>
            <SelectTrigger className="w-48" data-testid="mint-scope"><SelectValue /></SelectTrigger>
            <SelectContent>{SCOPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Reason</Label>
          <Select value={mintReason} onValueChange={setMintReason}>
            <SelectTrigger className="w-44" data-testid="mint-reason"><SelectValue /></SelectTrigger>
            <SelectContent>{REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Duration</Label>
          <Select value={mintDuration} onValueChange={setMintDuration}>
            <SelectTrigger className="w-36" data-testid="mint-duration"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="lifetime">Lifetime</SelectItem>
              <SelectItem value="expires">Expires</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Recipient / company</Label>
          <Input value={mintOrg} onChange={(e) => setMintOrg(e.target.value)} maxLength={120}
                 placeholder="e.g. Acme Corp" className="w-52" data-testid="mint-org" />
        </div>
        <div className="min-w-[16rem] flex-1 space-y-1.5">
          <Label>Reason detail / note</Label>
          <Input value={mintNote} onChange={(e) => setMintNote(e.target.value)} maxLength={500}
                 placeholder="Why these keys exist — e.g. Q4 partner onboarding for Dana's team"
                 data-testid="mint-note" />
        </div>
        {mintDuration === "expires" && (
          <div className="space-y-1.5">
            <Label>Expires on</Label>
            <Input type="date" value={mintExpires} onChange={(e) => setMintExpires(e.target.value)} className="w-44" data-testid="mint-expires" />
          </div>
        )}
        <Button onClick={mint} disabled={minting} data-testid="mint-button">
          {minting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />} Mint keys
        </Button>
        <div className="ml-auto flex flex-wrap gap-2 text-xs">
          {["available", "issued", "active", "superseded", "revoked"].map((s) => (
            <span key={s} className="rounded-md bg-secondary/60 px-2 py-1" data-testid={`license-count-${s}`}>{s}: <b>{counts[s] || 0}</b></span>
          ))}
        </div>
      </Card>

      {lastBatch && (
        <Card className="mt-4 flex flex-wrap items-center gap-3 border-emerald-200 bg-emerald-50/60 p-4" data-testid="mint-result">
          <div className="text-sm">
            <b>{lastBatch.minted} {TIER_LABEL[lastBatch.tier_key]} key{lastBatch.minted === 1 ? "" : "s"} minted</b>
            {lastBatch.recipient_org ? <> for <b>{lastBatch.recipient_org}</b></> : null}
            {lastBatch.note ? <span className="block text-xs text-muted-foreground">{lastBatch.note}</span> : null}
            {lastBatch.redeem_by ? <span className="block text-xs text-amber-700">Must be redeemed by {String(lastBatch.redeem_by).slice(0, 10)}</span> : null}
          </div>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button size="sm" variant="outline" disabled={exporting === lastBatch.batch_id}
                    onClick={() => download({ batch_id: lastBatch.batch_id }, lastBatch.batch_id)}
                    data-testid="mint-download-csv">
              <FileSpreadsheet className="mr-1.5 h-4 w-4" />{exporting === lastBatch.batch_id ? "Exporting…" : "Download CSV"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEmailFor(lastBatch)} data-testid="mint-email-batch">
              <Mail className="mr-1.5 h-4 w-4" />Email to company
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setLastBatch(null)} data-testid="mint-result-dismiss">Dismiss</Button>
          </div>
        </Card>
      )}

      {batches.length > 0 && (
        <Card className="mt-4 p-5" data-testid="license-batches">
          <h3 className="text-sm font-[680]">Recent batches</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">Every mass mint stays traceable — re-export or email a batch any time, or revoke the whole thing if a shared file leaks.</p>
          <div className="mt-3 divide-y">
            {batches.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center gap-3 py-2.5 text-sm" data-testid={`license-batch-${b.id}`}>
                <div className="min-w-[14rem] flex-1">
                  <b>{b.quantity} {TIER_LABEL[b.tier_key] || b.tier_key}</b>
                  {b.recipient_org ? <> · {b.recipient_org}</> : null}
                  <span className="ml-2 text-xs text-muted-foreground">{String(b.created_at).slice(0, 10)} · {b.remaining} unclaimed</span>
                  {b.notes ? <span className="block text-xs text-muted-foreground">{b.notes}</span> : null}
                  <span className="block text-[11px] text-muted-foreground">
                    {SCOPE_LABEL[b.redeem_scope] || "Inventory"}
                    {b.redeem_by ? ` · redeem by ${String(b.redeem_by).slice(0, 10)}` : b.redeem_scope === "claimable" ? " · no expiry" : ""}
                    {" · "}{b.export_count > 0 ? `Exported ${b.export_count}×` : "Not exported yet"}
                    {b.emailed_to ? ` · emailed to ${b.emailed_to}` : ""}
                    {b.revoked_count > 0 ? ` · ${b.revoked_count} revoked` : ""}
                  </span>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" disabled={!b.remaining}
                          onClick={() => openConvert(b.redeem_scope === "claimable" ? "inventory" : "claimable", b)}
                          title={b.remaining ? `Convert the ${b.remaining} unclaimed key(s) in this batch` : "No unclaimed keys left"}
                          data-testid={`batch-convert-${b.id}`}>
                    {b.redeem_scope === "claimable"
                      ? <><Archive className="mr-1.5 h-4 w-4" />To inventory</>
                      : <><Share2 className="mr-1.5 h-4 w-4" />To claimable</>}
                  </Button>
                  <Button size="sm" variant="ghost" disabled={exporting === b.id}
                          onClick={() => download({ batch_id: b.id }, b.id)} data-testid={`batch-export-${b.id}`}>
                    <FileSpreadsheet className="mr-1.5 h-4 w-4" />CSV
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEmailFor({ ...b, batch_id: b.id })} data-testid={`batch-email-${b.id}`}>
                    <Mail className="mr-1.5 h-4 w-4" />Email
                  </Button>
                  <Button size="sm" variant="ghost" className="text-destructive" disabled={!b.remaining}
                          onClick={() => setRevokeFor(b)} data-testid={`batch-revoke-${b.id}`}>
                    <Ban className="mr-1.5 h-4 w-4" />Revoke all
                  </Button>
                </div>
              </div>
            ))}
          </div>
          {batchTotal > BATCH_PAGE_SIZE && (
            <div className="mt-3 flex items-center justify-end gap-2 border-t pt-3">
              <span className="text-xs text-muted-foreground" data-testid="batch-page-label">
                {batchTotal.toLocaleString()} batches · page {batchPage} of {Math.ceil(batchTotal / BATCH_PAGE_SIZE)}
              </span>
              <Button size="icon" variant="outline" className="h-8 w-8" disabled={batchPage <= 1}
                      onClick={() => setBatchPage((p) => p - 1)} data-testid="batch-prev-page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="outline" className="h-8 w-8"
                      disabled={batchPage >= Math.ceil(batchTotal / BATCH_PAGE_SIZE)}
                      onClick={() => setBatchPage((p) => p + 1)} data-testid="batch-next-page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Filters */}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search key, scope, reason, recipient, holder…" className="w-[22rem]" data-testid="license-search" />
        </div>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
          <SelectTrigger className="w-40" data-testid="license-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={scope} onValueChange={(v) => { setScope(v); setPage(1); }}>
          <SelectTrigger className="w-40" data-testid="license-scope-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All scopes</SelectItem>
            <SelectItem value="inventory">Inventory</SelectItem>
            <SelectItem value="assigned">Assigned</SelectItem>
            <SelectItem value="claimable">Claimable</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tier} onValueChange={(v) => { setTier(v); setPage(1); }}>
          <SelectTrigger className="w-40" data-testid="license-tier-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            {TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          {total > EXPORT_CAP && <span className="text-xs text-muted-foreground">First {EXPORT_CAP.toLocaleString()} rows — filter to narrow</span>}
          <Button size="sm" variant="outline" disabled={exporting === "filters" || total === 0}
                  onClick={() => download({ status, tier, scope, q })} data-testid="license-export-csv">
            <FileSpreadsheet className="mr-1.5 h-4 w-4" />
            {exporting === "filters" ? "Exporting…" : `Export ${Math.min(total, EXPORT_CAP).toLocaleString()} to CSV`}
          </Button>
        </div>
      </div>

      {/* Key Conversion — retarget unclaimed stock without minting again */}
      {sel.length > 0 && (
        <Card className="mt-4 flex flex-wrap items-center gap-3 border-primary/40 bg-primary/5 p-3.5" data-testid="license-convert-bar">
          <span className="text-sm font-[640] text-foreground" data-testid="license-selected-count">
            {sel.length} key{sel.length === 1 ? "" : "s"} selected
          </span>
          <span className="text-xs text-muted-foreground">Only unclaimed keys can be converted.</span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => openConvert("claimable")} data-testid="license-convert-claimable">
              <Share2 className="mr-1.5 h-4 w-4" /> Convert to Claimable
            </Button>
            <Button size="sm" variant="outline" onClick={() => openConvert("inventory")} data-testid="license-convert-inventory">
              <Archive className="mr-1.5 h-4 w-4" /> Convert to Inventory
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSel([])} data-testid="license-clear-selection">Clear</Button>
          </div>
        </Card>
      )}

      {converted && (
        <Card className="mt-4 flex flex-wrap items-center gap-3 border-emerald-300 bg-emerald-50/70 p-3.5" data-testid="license-converted-strip">
          <span className="text-sm text-emerald-900">
            <b>{converted.count} key{converted.count === 1 ? "" : "s"}</b> converted to {SCOPE_LABEL[converted.scope]}
            {converted.scope === "claimable" ? " — anyone holding a key can now redeem it." : " — these keys can no longer be self-redeemed."}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={exporting === "converted"}
                    onClick={() => download({ ids: converted.ids.join(",") }, "converted")}
                    data-testid="license-export-converted">
              <FileSpreadsheet className="mr-1.5 h-4 w-4" />{exporting === "converted" ? "Exporting…" : "Download these keys (CSV)"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConverted(null)} data-testid="license-converted-dismiss">Dismiss</Button>
          </div>
        </Card>
      )}

      <Card className="mt-4 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <input type="checkbox" className="h-4 w-4 cursor-pointer accent-primary"
                       checked={allSelected} disabled={eligible.length === 0}
                       onChange={toggleAll} aria-label="Select all convertible keys on this page"
                       data-testid="license-select-all" />
              </TableHead>
              {SORT_COLUMNS.map((col) => (
                <SortHead key={col.key} col={col} sort={sort} dir={dir} onSort={toggleSort} />
              ))}
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null
              ? <TableRow><TableCell colSpan={10} className="py-10 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
              : rows.length === 0
                ? <TableRow><TableCell colSpan={10} className="py-10 text-center text-sm text-muted-foreground">No keys match.</TableCell></TableRow>
                : rows.map((k) => (
                  <TableRow key={k.id} data-testid={`license-row-${k.id}`}>
                    <TableCell>
                      <input type="checkbox" className="h-4 w-4 cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-40"
                             checked={sel.includes(k.id)} disabled={!convertible(k)}
                             onChange={() => toggleOne(k.id)}
                             title={convertible(k) ? "Select for conversion" : "Only unclaimed inventory or claimable keys can be converted"}
                             aria-label={`Select key ${k.formatted}`}
                             data-testid={`license-select-${k.id}`} />
                    </TableCell>
                    <TableCell><code className="font-mono text-xs tracking-wider">{k.formatted}</code></TableCell>
                    <TableCell>{k.tier_name}</TableCell>
                    <TableCell><Pill tone={STATUS_TONE[k.status]}>{k.status}</Pill></TableCell>
                    <TableCell><Pill tone={SCOPE_TONE[k.redeem_scope]} testId={`license-scope-${k.id}`}>{SCOPE_LABEL[k.redeem_scope] || "—"}</Pill></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{REASON_LABEL[k.reason_issued] || (k.reason_issued || "—")}</TableCell>
                    <TableCell className="max-w-[16rem] text-xs" data-testid={`license-note-${k.id}`}>
                      {k.recipient_org ? <span className="font-[640] text-foreground">{k.recipient_org}</span> : null}
                      {k.notes ? <span className="block truncate text-muted-foreground" title={k.notes}>{k.notes}</span> : null}
                      {!k.recipient_org && !k.notes ? <span className="text-muted-foreground">—</span> : null}
                    </TableCell>
                    <TableCell className="text-sm">{k.holder_email ? <span>{k.holder_name}<span className="block text-xs text-muted-foreground">{k.holder_email}</span></span> : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {String(k.issued_at).slice(0, 10)}
                      {k.expires_at && <span className="block text-amber-600">exp {String(k.expires_at).slice(0, 10)}</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copy(k.formatted)} title="Copy" data-testid={`license-copy-${k.id}`}><Copy className="h-4 w-4" /></Button>
                        {(k.status === "available" || k.status === "issued") && (
                          <>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setAssignFor(k)} title="Issue to member" data-testid={`license-assign-${k.id}`}><UserPlus className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => revoke(k.id)} title="Revoke" data-testid={`license-revoke-${k.id}`}><Ban className="h-4 w-4" /></Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </Card>

      {pages > 1 && (
        <div className="mt-4 flex items-center justify-end gap-2">
          <span className="text-sm text-muted-foreground">Page {page} of {pages}</span>
          <Button size="icon" variant="outline" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="icon" variant="outline" className="h-8 w-8" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}

      <AssignDialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)} onAssign={assign} />
      <AlertDialog open={!!revokeFor} onOpenChange={(o) => !o && setRevokeFor(null)}>
        <AlertDialogContent data-testid="batch-revoke-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke {revokeFor?.remaining} unclaimed key{revokeFor?.remaining === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Every unredeemed key in this batch{revokeFor?.recipient_org ? ` for ${revokeFor.recipient_org}` : ""} stops working immediately — use this if the file you sent has leaked. Keys members already redeemed keep working, and this can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="batch-revoke-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={revokeBatch} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="batch-revoke-confirm">Revoke all</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <EmailBatchDialog batch={emailFor} onClose={() => setEmailFor(null)} onSent={() => { setEmailFor(null); loadBatches(); }} />

      {/* Convert dialog — the claimable direction needs a claim window decision */}
      <Dialog open={!!convertTo && convertCount > 0} onOpenChange={(o) => { if (!converting && !o) { setConvertTo(null); setConvertBatch(null); } }}>
        <DialogContent data-testid="license-convert-dialog">
          <DialogHeader>
            <DialogTitle>
              Convert {convertCount} key{convertCount === 1 ? "" : "s"} to {convertTo === "claimable" ? "Claimable" : "Inventory"}?
            </DialogTitle>
            <DialogDescription>
              {convertBatch ? `Every unclaimed key in this ${convertBatch.quantity}-key batch${convertBatch.recipient_org ? ` for ${convertBatch.recipient_org}` : ""}. ` : ""}
              {convertTo === "claimable"
                ? "Claimable keys can be redeemed by anyone who holds the code, once each. Use this for keys you're about to share with a partner or hand out at an event."
                : "Inventory keys can't be self-redeemed — you assign them to a member yourself. Any claim window on these keys is cleared."}
            </DialogDescription>
          </DialogHeader>
          {convertTo === "claimable" && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Must be redeemed by</Label>
                <Input type="date" value={convertExpiry} disabled={convertNoExpiry}
                       onChange={(e) => setConvertExpiry(e.target.value)}
                       data-testid="license-convert-redeem-by" />
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input type="checkbox" className="h-4 w-4 cursor-pointer accent-primary"
                       checked={convertNoExpiry}
                       onChange={(e) => setConvertNoExpiry(e.target.checked)}
                       data-testid="license-convert-no-expiry" />
                No expiry — these keys stay claimable forever
              </label>
              <p className="rounded-md border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-900">
                A key with no expiry can still be redeemed years from now by anyone who finds the file. Revoke the batch if a shared list ever leaks.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setConvertTo(null); setConvertBatch(null); }} disabled={converting}>Cancel</Button>
            <Button onClick={runConvert} disabled={converting} data-testid="license-convert-confirm">
              {converting ? "Converting…" : `Convert ${convertCount} key${convertCount === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
